import {
    ValueFormatterParams,
    ColDef,
    ValueFormatterFunc,
    ValueGetterParams,
} from 'ag-grid-community';
import {
    WdxCurrencyOptions,
    WdxNumberService,
    WdxDateTimeService,
    WdxDateFormat,
    TranslationsService,
    LocalisedString,
    FormatCurrencyPipe,
    Locale,
} from '@wdx/shared/utils';
import { AG_GRID_VALUE_GETTER } from './value-getter-functions';

export interface WdxAgGridCurrencyField {
    value: number;
    currency: WdxCurrencyOptions;
}

export const PRECISION_PERCENTAGE_VALUE_FORMATTER: (
    precision?: number,
) => ValueFormatterFunc =
    (precision = 1) =>
    (p: ValueFormatterParams<any, number>) =>
        typeof p.value === 'number' ? `${p.value.toFixed(precision)}%` : '';

export const PERCENTAGE_VALUE_FORMATTER: ValueFormatterFunc =
    PRECISION_PERCENTAGE_VALUE_FORMATTER(1);

export const CURRENCY_VALUE_FORMATTER = (numberService: WdxNumberService) =>
    ((params: ValueFormatterParams<any, WdxAgGridCurrencyField>) => {
        return typeof params.value?.value === 'number' && params.value?.currency
            ? numberService.formatCurrency(
                  params.value?.value,
                  params.value?.currency,
              ) || ''
            : '';
    }) as ValueFormatterFunc;

export const LOCALISED_CURRENCY_VALUE_FORMATTER = (
    numberService: WdxNumberService,
    getLocale?: () => Locale | undefined,
) =>
    ((params: ValueFormatterParams<any, WdxAgGridCurrencyField>) => {
        const locale = getLocale?.();

        return typeof params.value?.value === 'number' && params.value?.currency
            ? numberService.formatLocalisedCurrency(params.value.value, {
                  ...params.value.currency,
                  locale: locale?.code,
                  groupSeparator: locale?.numberFormat?.numberGroupSeparator,
                  decimalSeparator:
                      locale?.numberFormat?.numberDecimalSeparator,
              }) || ''
            : '';
    }) as ValueFormatterFunc;

export const DATE_CELL_VALUE_FORMATTER = (
    dateTimeService: WdxDateTimeService,
    format = WdxDateFormat.AbsoluteDate,
) =>
    ((params: ValueFormatterParams) => {
        const value = AG_GRID_VALUE_GETTER(
            params as unknown as ValueGetterParams,
        );
        return value
            ? dateTimeService.convertDateToViewFriendlyFormat(value, { format })
            : '';
    }) as ValueFormatterFunc;

export const DATE_CELL_COL_DEFS: ColDef = {
    minWidth: 100,
    maxWidth: 125,
};

export const TRANSLATE_CELL_VALUE =
    (translationsService: TranslationsService) =>
    (params: ValueFormatterParams) => {
        const value: string | LocalisedString = AG_GRID_VALUE_GETTER(
            params as unknown as ValueGetterParams,
        );
        if (!value) {
            return '';
        }
        if (typeof value === 'string') {
            return translationsService.translateTokenisedString(
                value as string,
            );
        }
        return (
            ((value as LocalisedString).value
                ? (value as LocalisedString).value
                : translationsService.translateTokenisedString(
                      (value as LocalisedString).key as string,
                  )) ?? ''
        );
    };

export const SHORT_CURRENCY_VALUE_FORMATTER =
    (value = 'value', isoCode = 'isoCode') =>
    (params: ValueFormatterParams) => {
        const val = AG_GRID_VALUE_GETTER(
            params as unknown as ValueGetterParams,
        );
        return (
            (val
                ? new FormatCurrencyPipe().transform(
                      val?.[value],
                      val?.[isoCode],
                  )
                : '') ?? ''
        );
    };

export const FORM_CURRENCY_VALUE_FORMATTER = SHORT_CURRENCY_VALUE_FORMATTER(
    'amount',
    'isoCode',
);
